import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, ChevronUp, ChevronDown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

export default function LabCategories() {
  const { can } = useAuth();
  const manage = can("lab.tests.manage");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", active: true });
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try { const { data } = await api.get("/lab/categories-master"); setItems(data.items); }
    catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ name: "", active: true }); setFormError(""); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, active: c.active }); setFormError(""); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError("Category name is required."); return; }
    setBusy(true); setFormError("");
    try {
      if (editing) await api.patch(`/lab/categories-master/${editing.category_id}`, form);
      else await api.post("/lab/categories-master", form);
      toast.success(editing ? "Category updated." : "Category added.");
      setOpen(false); load();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };

  const toggle = async (c) => {
    try { await api.patch(`/lab/categories-master/${c.category_id}`, { active: !c.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const move = async (idx, dir) => {
    const arr = [...items];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try { await api.post("/lab/categories-master/reorder", { ordered_ids: arr.map((c) => c.category_id) }); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  if (!can("lab.tests.view")) return <NoAccess what="Lab test categories" />;

  return (
    <div data-testid="lab-categories-page">
      <PageHeader overline="Laboratory" title="Test Categories"
        subtitle="Organise the test database into diagnostic categories. Drag priority with the arrows."
        actions={manage && <button onClick={openNew} className={primaryBtnCls} data-testid="add-category-btn"><Plus className="h-4 w-4" /> Add category</button>} />
      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="lab-categories-table" columns={["#", "Category", "Status", manage ? "Actions" : ""]}
          rows={items} empty={<EmptyState title="No categories yet" subtitle="Add your first diagnostic category." testid="lab-categories-empty" />}
          renderRow={(c, idx) => (
            <tr key={c.category_id} data-testid={`lab-category-row-${c.category_id}`}>
              <Td className="text-slate-400">{idx + 1}</Td>
              <Td className="font-medium text-slate-900">{c.name}</Td>
              <Td><span className={`text-xs font-medium ${c.active ? "text-emerald-600" : "text-slate-400"}`}>{c.active ? "Active" : "Inactive"}</span></Td>
              {manage && <Td>
                <div className="flex items-center gap-2">
                  <button onClick={() => move(idx, -1)} className={ghostBtnCls} data-testid={`cat-up-${c.category_id}`}><ChevronUp className="h-4 w-4" /></button>
                  <button onClick={() => move(idx, 1)} className={ghostBtnCls} data-testid={`cat-down-${c.category_id}`}><ChevronDown className="h-4 w-4" /></button>
                  <button onClick={() => openEdit(c)} className={ghostBtnCls} data-testid={`edit-category-${c.category_id}`}>Edit</button>
                  <button onClick={() => toggle(c)} className={ghostBtnCls} data-testid={`toggle-category-${c.category_id}`}>{c.active ? "Deactivate" : "Activate"}</button>
                </div>
              </Td>}
            </tr>
          )} />
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit category" : "Add category"} testid="lab-category-modal">
        <form onSubmit={save} className="space-y-5">
          <Field label="Category name *"><input className={inputCls} data-testid="category-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="category-active" /> Active</label>
          {formError && <p className="text-sm text-red-600" data-testid="category-form-error">{formError}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
            <button disabled={busy} className={primaryBtnCls} data-testid="save-category-btn">{busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
